import { useMemo, useState, type ReactNode } from 'react'
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { EmptyState, ErrorState, Skeleton } from './display'

export interface Column<T> {
  key: string
  header: ReactNode
  cell: (row: T) => ReactNode
  className?: string
  headerClassName?: string
  align?: 'left' | 'right' | 'center'
  /** Makes the column sortable on the loaded rows: the value to sort by. With server-side sorting
   *  (`onSortChange` on the table) only its presence matters. */
  sortValue?: (row: T) => number | string
}

export interface SortState { key: string; dir: 'asc' | 'desc' }

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  loading,
  error,
  onRetry,
  onRowClick,
  empty,
  className,
  dense,
  rowClassName,
  sort: sortProp,
  onSortChange,
}: {
  columns: Column<T>[]
  rows: T[] | undefined
  rowKey: (r: T) => string
  loading?: boolean
  error?: unknown
  onRetry?: () => void
  onRowClick?: (r: T) => void
  empty?: ReactNode
  className?: string
  dense?: boolean
  rowClassName?: (r: T) => string | undefined
  /** Server-side sorting: pass the current sort and a handler; rows are then shown as given. */
  sort?: SortState | null
  onSortChange?: (s: SortState) => void
}) {
  // Sorting is on the rows already loaded; null keeps the order the API returned.
  const [localSort, setLocalSort] = useState<SortState | null>(null)
  const controlled = !!onSortChange
  const sort = controlled ? (sortProp ?? null) : localSort
  const sorted = useMemo(() => {
    if (controlled) return rows
    const col = sort && columns.find((c) => c.key === sort.key)
    if (!rows || !sort || !col?.sortValue) return rows
    const val = col.sortValue
    const dir = sort.dir
    return [...rows].sort((a, b) => {
      const x = val(a)
      const y = val(b)
      const cmp = typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y))
      return dir === 'asc' ? cmp : -cmp
    })
  }, [rows, columns, sort, controlled])
  if (error) return <ErrorState error={error} onRetry={onRetry} />
  const pad = dense ? 'px-3 py-2' : 'px-4 py-3'
  // First click sorts high to low (what you usually want for amounts), the next low to high.
  const next = (key: string): SortState => (sort?.key === key ? { key, dir: sort.dir === 'desc' ? 'asc' : 'desc' } : { key, dir: 'desc' })
  const toggle = (key: string) => (controlled ? onSortChange!(next(key)) : setLocalSort(next(key)))
  return (
    <div className={cn('scroll-thin overflow-x-auto', className)}>
      <table className="w-full min-w-[640px] border-collapse text-left text-sm">
        <thead>
          <tr className="border-b border-ink-200 bg-ink-50/70">
            {columns.map((c) => (
              <th
                key={c.key}
                scope="col"
                aria-sort={c.sortValue ? (sort?.key === c.key ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none') : undefined}
                className={cn(
                  'px-4 py-2.5 text-xs font-semibold tracking-wide whitespace-nowrap text-ink-500 uppercase',
                  dense && 'px-3',
                  c.align === 'right' && 'text-right',
                  c.align === 'center' && 'text-center',
                  c.headerClassName,
                )}
              >
                {c.sortValue ? (
                  <button
                    type="button"
                    onClick={() => toggle(c.key)}
                    className={cn('inline-flex items-center gap-1 uppercase hover:text-ink-800', c.align === 'right' && 'flex-row-reverse', sort?.key === c.key && 'text-ink-800')}
                  >
                    {c.header}
                    {sort?.key === c.key ? (
                      sort.dir === 'asc' ? <ArrowUp className="h-3 w-3" aria-hidden /> : <ArrowDown className="h-3 w-3" aria-hidden />
                    ) : (
                      <ArrowUpDown className="h-3 w-3 opacity-40" aria-hidden />
                    )}
                  </button>
                ) : (
                  c.header
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-ink-100">
          {loading &&
            Array.from({ length: 6 }).map((_, i) => (
              <tr key={i}>
                {columns.map((c) => (
                  <td key={c.key} className={pad}>
                    <Skeleton className="h-4 w-3/4" />
                  </td>
                ))}
              </tr>
            ))}
          {!loading &&
            sorted?.map((r) => (
              <tr
                key={rowKey(r)}
                onClick={onRowClick ? () => onRowClick(r) : undefined}
                onKeyDown={
                  onRowClick
                    ? (e) => {
                        if (e.key === 'Enter') onRowClick(r)
                      }
                    : undefined
                }
                tabIndex={onRowClick ? 0 : undefined}
                className={cn('transition-colors', onRowClick && 'cursor-pointer hover:bg-brand-50/40 focus-visible:bg-brand-50/60', rowClassName?.(r))}
              >
                {columns.map((c) => (
                  <td
                    key={c.key}
                    className={cn(pad, 'align-middle text-ink-800', c.align === 'right' && 'text-right tabular', c.align === 'center' && 'text-center', c.className)}
                  >
                    {c.cell(r)}
                  </td>
                ))}
              </tr>
            ))}
        </tbody>
      </table>
      {!loading && rows && rows.length === 0 && (empty ?? <EmptyState title="Nothing here yet" />)}
    </div>
  )
}
